// File: src/components/PasswordChange.js
// Password change component

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { API_BASE_URL } from '../App';
import { useLanguage } from '../contexts/LanguageContext';

function PasswordChange({ token, onBack }) {
  const { t } = useLanguage();
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleChangePassword = async () => {
    setMessage('');
    setError('');

    if (!passwords.currentPassword || !passwords.newPassword) {
      setError(t('allFieldsRequired'));
      return;
    }

    if (passwords.newPassword !== passwords.confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      return;
    }

    if (passwords.newPassword.length < 6) {
      setError(t('passwordTooShort'));
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/users/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwords.currentPassword,
          newPassword: passwords.newPassword
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage(t('passwordChangedSuccessfully'));
        setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => onBack(), 2000);
      } else {
        setError(data.error || t('failedToChangePassword'));
      }
    } catch (error) {
      setError(t('connectionError'));
    }
  };

  return (
    <div className="app-shell flex items-center justify-center p-4">
      <div className="bg-paper rounded-lg p-8 max-w-md w-full border border-line">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-ink">{t('changePassword')}</h2>
          <button onClick={onBack} className="text-ink-muted hover:text-ink">
            <X className="w-6 h-6" />
          </button>
        </div>

        {message && (
          <div className="bg-green-50 dark:bg-green-500/20 border border-green-500 text-green-800 dark:text-green-200 px-4 py-3 rounded mb-4">
            {message}
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-ink-muted mb-2">{t('currentPassword')}</label>
            <input
              type="password"
              value={passwords.currentPassword}
              onChange={(e) => setPasswords({...passwords, currentPassword: e.target.value})}
              className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
            />
          </div>
          <div>
            <label className="block text-ink-muted mb-2">{t('newPassword')}</label>
            <input
              type="password"
              value={passwords.newPassword}
              onChange={(e) => setPasswords({...passwords, newPassword: e.target.value})}
              className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
            />
          </div>
          <div>
            <label className="block text-ink-muted mb-2">{t('confirmPassword')}</label>
            <input
              type="password"
              value={passwords.confirmPassword}
              onChange={(e) => setPasswords({...passwords, confirmPassword: e.target.value})}
              className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
            />
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={handleChangePassword}
            className="flex-1 bg-accent hover:opacity-90 text-accent-ink py-2 px-4 rounded font-semibold"
          >
            {t('changePassword')}
          </button>
          <button
            onClick={onBack}
            className="flex-1 bg-paper-alt hover:opacity-80 text-ink py-2 px-4 rounded font-semibold"
          >
            {t('cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PasswordChange;